import { Link } from "react-router-dom";
import { Star, Heart, Check, Footprints, Mountain } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useUser } from "../lib/userAuth";

// Down-size Unsplash/Pexels/internal-file URLs for fast card thumbnails
function thumbUrl(url, w = 600, q = 70) {
  if (!url) return url;
  if (url.includes("images.unsplash.com")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}w=${w}&q=${q}&auto=format&fit=crop`;
  }
  if (url.includes("images.pexels.com")) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}w=${w}&auto=compress&cs=tinysrgb`;
  }
  if (url.startsWith("/api/files/")) return `${url}?w=${w}&q=${q}`;
  return url;
}

export default function WaterfallCard({ wf, portrait = true, showVisited = true }) {
  const { user, signIn } = useUser();
  const [saved, setSaved] = useState(false);
  const [visited, setVisited] = useState(false);
  const [busy, setBusy] = useState(false);
  const src = thumbUrl(wf.image_urls?.[0]) || "https://images.unsplash.com/photo-1469125155630-7ed37e065743?w=600&q=70";

  // Build a short description for the card: short_blurb → first sentence of description → meta_description
  const cardBlurb = (() => {
    if (wf.short_blurb && wf.short_blurb.trim()) return wf.short_blurb.trim();
    const desc = (wf.description || "").trim();
    if (desc) {
      // First sentence (or first 140 chars) of description
      const firstSentence = desc.split(/(?<=[.!?])\s/)[0];
      if (firstSentence && firstSentence.length <= 180) return firstSentence;
      return desc.slice(0, 140).trim() + (desc.length > 140 ? "…" : "");
    }
    return (wf.meta_description || "").trim();
  })();

  useEffect(() => {
    if (!user) { setSaved(false); setVisited(false); return; }
    (async () => {
      try {
        const [b, v] = await Promise.all([
          api.get("/me/bookmarks/check", { params: { slugs: wf.slug } }),
          showVisited ? api.get("/me/visits/check", { params: { slugs: wf.slug } }) : Promise.resolve({ data: {} }),
        ]);
        setSaved(!!b.data[wf.slug]);
        setVisited(!!v.data[wf.slug]);
      } catch { /* ignore */ }
    })();
  }, [user, wf.slug, showVisited]);

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { signIn(null, `Sign in to bookmark ${wf.name} and build your own list of waterfalls to visit.`); return; }
    setBusy(true);
    try {
      const r = await api.post(`/me/bookmarks/${wf.slug}`);
      setSaved(r.data.bookmarked);
    } finally { setBusy(false); }
  };

  return (
    <Link to={`/waterfalls/${wf.slug}`} data-testid={`waterfall-card-${wf.slug}`} className="card-at block group">
      <div className={`relative overflow-hidden rounded-2xl bg-stone-100 ${portrait ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
        <img
          src={src}
          alt={wf.name}
          loading="lazy"
          decoding="async"
          width="600"
          height={portrait ? 800 : 450}
          sizes="(min-width: 1280px) 22vw, (min-width: 768px) 30vw, 48vw"
          className="w-full h-full object-cover"
        />
        <button
          aria-label={saved ? "Remove bookmark" : "Save"}
          onClick={toggleSave}
          disabled={busy}
          data-testid={`card-save-${wf.slug}`}
          className="heart-btn absolute top-3 right-3"
        >
          <Heart className={`w-4 h-4 ${saved ? "fill-red-500 text-red-500" : "text-stone-900"}`} />
        </button>
        {visited && (
          <span
            data-testid={`card-visited-${wf.slug}`}
            className="absolute top-3 left-3 bg-emerald-600 text-white text-[11px] font-bold rounded-full pl-1.5 pr-2.5 py-1 flex items-center gap-1 shadow"
            title="You've been here"
          >
            <Check className="w-3 h-3" /> Visited
          </span>
        )}
      </div>
      <div className="pt-3">
        <h3 className="text-[15px] font-extrabold text-stone-900 leading-tight truncate group-hover:underline">
          {wf.name}
        </h3>
        <p className="text-sm text-stone-500 mt-0.5 truncate">
          {[wf.city, wf.state, wf.country].filter(Boolean).slice(0, 2).join(", ")}
        </p>
        {cardBlurb && (
          <p
            className="mt-1.5 text-[13px] text-stone-600 leading-snug line-clamp-2"
            data-testid={`card-blurb-${wf.slug}`}
          >
            {cardBlurb}
          </p>
        )}
        <div className="mt-2 flex items-center gap-1.5 text-sm text-stone-700 flex-wrap">
          {wf.height_m > 0 && (
            <>
              <span className="inline-flex items-center gap-1 text-stone-700 font-semibold">
                <Mountain className="w-3.5 h-3.5 text-stone-500" />
                {Math.round(wf.height_m)}m
              </span>
              <span className="text-stone-300 mx-0.5">·</span>
            </>
          )}
          {wf.review_count > 0 ? (
            <>
              <Star className="w-4 h-4 fill-stone-900 text-stone-900" />
              <span className="font-semibold">{wf.rating?.toFixed(1)}</span>
              <span className="text-stone-500">({wf.review_count.toLocaleString()})</span>
            </>
          ) : (
            <span className="text-stone-400">No reviews yet</span>
          )}
          {wf.visit_count > 0 && (
            <>
              <span className="text-stone-300 mx-0.5">·</span>
              <Footprints className="w-4 h-4 text-stone-600" />
              <span className="text-stone-700">{wf.visit_count.toLocaleString()}</span>
              <span className="text-stone-500">been</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
